import type { Section } from '@/api/types/core/sections';
import type { SectionJson } from '@/api/types/core/schemaJson';
import type { Translation, TranslationCreate } from '@/api/types/core/translations';
import type { TranslationConflict, SchemaTranslationResponse } from '@/api/types/core/schemaTranslations';

/**
 * Utilitaires pour la gestion des sections
 */
export class SectionUtils {
  /**
   * Extrait le dernier segment d'un nom de section hiérarchique
   * @param fullName - Le nom complet de la section (ex: "parent.child.grandchild")
   * @returns Le dernier segment (ex: "grandchild")
   */
  static getLastSegment(fullName: string): string {
    if (!fullName) return '';
    const segments = fullName.split('.');
    return segments[segments.length - 1];
  }

  /**
   * Extrait le nom du parent d'une section
   * @param fullName - Le nom complet de la section (ex: "parent.child.grandchild")
   * @returns Le nom du parent (ex: "parent.child") ou null si c'est une section racine
   */
  static getParentName(fullName: string): string | null {
    if (!fullName || !fullName.includes('.')) return null;
    const segments = fullName.split('.');
    segments.pop(); // Enlever le dernier segment
    return segments.join('.');
  }

  /**
   * Calcule le niveau d'indentation d'une section
   * @param fullName - Le nom complet de la section
   * @returns Le niveau d'indentation (0 pour les sections racines)
   */
  static getIndentationLevel(fullName: string): number {
    if (!fullName) return 0;
    return fullName.split('.').length - 1;
  }

  /**
   * Alias pour getIndentationLevel (pour compatibilité)
   * @param fullName - Le nom complet de la section
   * @returns Le niveau d'indentation (0 pour les sections racines)
   */
  static getDepthLevel(fullName: string): number {
    return this.getIndentationLevel(fullName);
  }

  /**
   * Vérifie si un nom de section est valide
   * @param name - Le nom à vérifier
   * @returns true si le nom est valide
   */
  static isValidSectionName(name: string): boolean {
    if (!name || name.trim() === '') return false;
    
    // Regex pour valider : lettres, chiffres, underscores, pas de points
    const validNameRegex = /^[a-zA-Z0-9_]+$/;
    return validNameRegex.test(name.trim());
  }

  /**
   * Génère un nom de section unique en ajoutant un suffixe numérique si nécessaire
   * @param baseName - Le nom de base souhaité
   * @param existingNames - Liste des noms existants
   * @returns Un nom unique
   */
  static generateUniqueName(baseName: string, existingNames: string[]): string {
    if (!existingNames.includes(baseName)) {
      return baseName;
    }

    let counter = 1;
    let uniqueName = `${baseName}_${counter}`;
    
    while (existingNames.includes(uniqueName)) {
      counter++;
      uniqueName = `${baseName}_${counter}`;
    }
    
    return uniqueName;
  }

  /**
   * Construit le chemin complet d'une section à partir de son nom simple et de son parent
   * @param simpleName - Le nom simple de la section
   * @param parentName - Le nom complet du parent (optionnel)
   * @returns Le nom complet de la section
   */
  static buildFullName(simpleName: string, parentName?: string): string {
    if (!parentName) return simpleName;
    return `${parentName}.${simpleName}`;
  }

  /**
   * Vérifie si une section est un descendant d'une autre
   * @param childName - Le nom de la section enfant potentielle
   * @param parentName - Le nom de la section parent potentielle
   * @returns true si childName est un descendant de parentName
   */
  static isDescendantOf(childName: string, parentName: string): boolean {
    if (!childName || !parentName) return false;
    return childName.startsWith(parentName + '.');
  }

  /**
   * Vérifie si une section est un enfant direct d'une autre
   * @param childName - Le nom de la section enfant potentielle
   * @param parentName - Le nom de la section parent potentielle
   * @returns true si childName est un enfant direct de parentName
   */
  static isDirectChildOf(childName: string, parentName: string): boolean {
    if (!childName || !parentName) return false;
    
    const expectedPrefix = parentName + '.';
    if (!childName.startsWith(expectedPrefix)) return false;
    
    const remainingPath = childName.substring(expectedPrefix.length);
    return !remainingPath.includes('.'); // Pas de point = enfant direct
  }

  /**
   * Obtient tous les segments d'un chemin de section
   * @param fullName - Le nom complet de la section
   * @returns Un tableau des segments
   */
  static getPathSegments(fullName: string): string[] {
    if (!fullName) return [];
    return fullName.split('.');
  }

  /**
   * Reconstruit un nom de section en remplaçant un segment
   * @param fullName - Le nom complet original
   * @param segmentIndex - L'index du segment à remplacer
   * @param newSegment - Le nouveau segment
   * @returns Le nouveau nom complet
   */
  static replaceSegment(fullName: string, segmentIndex: number, newSegment: string): string {
    const segments = this.getPathSegments(fullName);
    if (segmentIndex < 0 || segmentIndex >= segments.length) {
      throw new Error('Index de segment invalide');
    }
    
    segments[segmentIndex] = newSegment;
    return segments.join('.');
  }

  /**
   * Génère tous les chemins parents nécessaires pour une section
   * @param fullName - Le nom complet de la section
   * @returns Un tableau des chemins parents (du plus proche au plus éloigné)
   */
  static generateParentPaths(fullName: string): string[] {
    const segments = this.getPathSegments(fullName);
    const parentPaths: string[] = [];
    
    // Générer tous les chemins parents
    for (let i = segments.length - 1; i > 0; i--) {
      const parentPath = segments.slice(0, i).join('.');
      parentPaths.push(parentPath);
    }
    
    return parentPaths;
  }

  /**
   * Valide la cohérence d'une hiérarchie de sections
   * @param sections - Liste des sections avec leurs noms
   * @returns Un objet avec les erreurs détectées
   */
  static validateHierarchy(sections: { name: string; id: number }[]): {
    orphaned: { name: string; id: number; missingParent: string }[];
    duplicates: { name: string; ids: number[] }[];
    invalidNames: { name: string; id: number }[];
  } {
    const sectionNames = new Set(sections.map(s => s.name));
    const nameToIds = new Map<string, number[]>();
    
    // Détecter les doublons
    sections.forEach(section => {
      if (!nameToIds.has(section.name)) {
        nameToIds.set(section.name, []);
      }
      nameToIds.get(section.name)!.push(section.id);
    });
    
    const duplicates = Array.from(nameToIds.entries())
      .filter(([, ids]) => ids.length > 1)
      .map(([name, ids]) => ({ name, ids }));
    
    // Détecter les noms invalides
    const invalidNames = sections.filter(section => 
      !this.isValidSectionName(this.getLastSegment(section.name))
    );
    
    // Détecter les sections orphelines
    const orphaned: { name: string; id: number; missingParent: string }[] = [];
    
    sections.forEach(section => {
      const parentName = this.getParentName(section.name);
      if (parentName && !sectionNames.has(parentName)) {
        orphaned.push({
          name: section.name,
          id: section.id,
          missingParent: parentName
        });
      }
    });
    
    return {
      orphaned,
      duplicates,
      invalidNames
    };
  }

  /**
   * Trie les sections par ordre hiérarchique
   * @param sections - Liste des sections à trier
   * @returns Les sections triées
   */
  static sortHierarchically<T extends { name: string; ordre?: number }>(sections: T[]): T[] {
    return sections.sort((a, b) => {
      // D'abord par profondeur (sections racines en premier)
      const depthA = this.getIndentationLevel(a.name);
      const depthB = this.getIndentationLevel(b.name);
      
      if (depthA !== depthB) {
        return depthA - depthB;
      }
      
      // Ensuite par ordre alphabétique du chemin complet
      const pathComparison = a.name.localeCompare(b.name);
      if (pathComparison !== 0) {
        return pathComparison;
      }
      
      // Enfin par ordre numérique si disponible
      const orderA = a.ordre ?? 0;
      const orderB = b.ordre ?? 0;
      return orderA - orderB;
    });
  }

  /**
   * Filtre les sections pour n'afficher que celles visibles selon l'état de collapse
   * @param sections - Liste de toutes les sections
   * @param collapsedSections - Set des sections repliées
   * @returns Les sections visibles
   */
  static filterVisibleSections<T extends { name: string }>(
    sections: T[], 
    collapsedSections: Set<string>
  ): T[] {
    return sections.filter(section => {
      // Vérifier si un parent est replié
      const parentPaths = this.generateParentPaths(section.name);
      return !parentPaths.some(parentPath => collapsedSections.has(parentPath));
    });
  }

  /**
   * Analyse les sections d'import pour détecter les conflits
   * @param importSections - Sections à importer
   * @param existingSections - Sections existantes
   * @returns Analyse des conflits
   */
  static analyzeImportSections(importSections: SectionJson[], existingSections: Section[]): {
    new_sections: SectionJson[];
    existing_sections: { imported: SectionJson; existing: Section }[];
    similar_sections: { imported: SectionJson; existing: Section }[];
    hierarchy_conflicts: { imported: SectionJson; issue: string }[];
  } {
    const existingNames = new Set(existingSections.map(s => s.name));
    const new_sections: SectionJson[] = [];
    const existing_sections: { imported: SectionJson; existing: Section }[] = [];
    const similar_sections: { imported: SectionJson; existing: Section }[] = [];
    const hierarchy_conflicts: { imported: SectionJson; issue: string }[] = [];
    
    importSections.forEach(importSection => {
      if (existingNames.has(importSection.id)) {
        const existing = existingSections.find(s => s.name === importSection.id)!;
        existing_sections.push({ imported: importSection, existing });
      } else {
        new_sections.push(importSection);
      }
    });
    
    return {
      new_sections,
      existing_sections,
      similar_sections,
      hierarchy_conflicts
    };
  }

  /**
   * Détecte les conflits de traductions
   * @param importTranslations - Traductions à importer
   * @param existingTranslations - Traductions globales existantes
   * @param existingSchemaTranslations - Traductions schéma existantes
   * @param existingSections - Sections existantes pour obtenir les noms
   * @param schemaUuid - UUID du schéma pour le contexte
   * @returns Conflits de traductions
   */
  static detectTranslationConflicts(
    importTranslations: TranslationCreate[], 
    existingTranslations: Translation[], 
    existingSchemaTranslations: SchemaTranslationResponse[],
    existingSections: Section[]
  ): TranslationConflict[] {
    const conflicts: TranslationConflict[] = [];
    const existingGlobalMap = new Map<string, Translation>();
    const existingSchemaMap = new Map<string, SchemaTranslationResponse>();
    const sectionMap = new Map<number, string>();
    
    // Créer une map des sections pour obtenir les noms
    existingSections.forEach(s => {
      sectionMap.set(s.id, s.name);
    });
    
    // Créer des maps pour les traductions existantes
    existingTranslations.forEach(t => {
      const key = `${t.reference_id}-${t.language_code}`;
      existingGlobalMap.set(key, t);
    });
    
    existingSchemaTranslations.forEach(t => {
      const key = `${t.section_id}-${t.language_code}`;
      existingSchemaMap.set(key, t);
    });
    
    importTranslations.forEach(importTranslation => {
      const key = `${importTranslation.reference_id}-${importTranslation.language_code}`;
      const existingSchema = existingSchemaMap.get(key);
      const existingGlobal = existingGlobalMap.get(key);
      const sectionName = sectionMap.get(importTranslation.reference_id) || `section_${importTranslation.reference_id}`;
      
      // Prioriser la traduction schéma si elle existe
      const existing = existingSchema || existingGlobal;
      const source = existingSchema ? 'schema' : 'global';
      
      if (existing) {
        // Vérifier si les traductions sont réellement différentes
        const existingTitle = existing.titre?.trim() || '';
        const importedTitle = importTranslation.titre?.trim() || '';
        const existingDesc = existing.description?.trim() || '';
        const importedDesc = importTranslation.description?.trim() || '';
        
        // Seulement créer un conflit si les traductions sont différentes
        if (existingTitle !== importedTitle || existingDesc !== importedDesc) {
          conflicts.push({
            section_name: sectionName,
            language_code: importTranslation.language_code,
            existing_translation: {
              source: source,
              titre: existing.titre,
              description: existing.description,
              id: existing.id
            },
            imported_translation: {
              titre: importTranslation.titre,
              description: importTranslation.description
            },
            resolution: existingSchema ? 'update_schema_translation' : 'create_schema_specific'
          });
        }
      }
    });
    
    return conflicts;
  }
} 